<script setup lang="ts">
import {computed, ref} from 'vue';
import {useRouter} from "#app";
import useNews from '~/composables/useNews'

const router = useRouter();

const news = ref([]);
const props = defineProps<{
    attr: AttrNews;
}>();

let perPageNews = props.attr.quantity ?? 6;

const {getNewsByTag} = useNews();

interface AttrNews {
    tags: string;
    quantity: number;
}

onBeforeMount(async () => {
    news.value = await getNewsByTag(props.attr.tags, perPageNews);
});

const formatData = (data: string | null) => {
    if (!data) {
        return "";
    }
    const regexDate = /^\d{4}-\d{2}-\d{2}$/;
    if (!regexDate.test(data)) {
        console.error("Formato de data inválido. Esperado: 'YYYY-MM-DD'");
        return "";
    }
    const dateCorrect = new Date(new Date(data).getTime() + 24 * 60 * 60 * 1000);
    const options: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    };
    const dateFormat = dateCorrect.toLocaleDateString('pt-BR', options);
    return dateFormat !== "Invalid Date" && dateFormat !== "" ? dateFormat.replace(',', '') : "";
};

const redirectToNews = (newsItem: any) => {
    try {
        router.push(`/noticias/${encodeURIComponent(newsItem.slug)}`);
    } catch (error) {
        console.log(error)
    }
};

const displayTag = computed(() => {
    if (props.attr?.tags && props.attr.tags.length > 30) {
        return props.attr.tags.substring(0, 20) + '...';
    }
    return props.attr?.tags || '';
});

</script>

<template>
    <div v-for="(item, index) in news" :key="index" class="block">
        <div
            class="
                w-56 xl:w-60 2xl:w-72
                rounded-b-lg rounded-t-lg
                bg-white
                text-center
            "
            style="box-shadow: -2px 0 4px 0 #DADADA, 2px 0 4px 0 #DADADA, 0 4px 4px 0 #DADADA;"
        >
            <NuxtLink :to="'/noticias/' +  encodeURIComponent(item.slug)" rel="noopener" prefetch class="relative">
                <img
                    :src="item.thumbnail"
                    :alt="'Thumbnail da notícia ' + item.title"
                    class="w-full rounded-t-lg object-cover h-[135px] xl:h-[150px] 2xl:h-[170px]"
                />

                <div
                    class="flex items-center justify-between text-gray-500 py-3 px-5 space-x-1"
                >
                    <p
                        class="w-auto h-auto text-xs text-color-primary bg-color-primary/5 px-2 py-0.5 rounded-lg text-start truncate">
                        {{ displayTag }}
                    </p>

                    <p class="text-xs xl:text-sm text-left">
                        {{ formatData(item.publication_date) }}
                    </p>
                </div>

                <div class="px-5 text-left h-[100px] xl:h-[120px] 2xl:h-[130px]">
                    <h2
                        class="
                            text-sm xl:text-lg
                            font-bold
                            text-color-primary
                            uppercase
                            line-clamp-1
                        "
                    >
                        {{ item.title }}
                    </h2>

                    <h2
                        class="
                            mt-1
                            text-xs xl:text-sm
                            text-[#263238]
                            line-clamp-custom
                        "
                    >
                        {{ item.subtitle }}
                    </h2>
                </div>
            </NuxtLink>
        </div>
    </div>

    <div v-for="(item, index) in news" :key="index" class="hidden lg:hidden">
        <div class="cursor-pointer" @click="redirectToNews(item)">
            <div
                class="rounded-b-lg shadow-md hover:shadow-xl hover:-translate-y-1 transition-transform h-[342px] bg-white rounded-t-lg w-[276px]">
                <div class="text-[#0400A1] place-items-around lg:w-full grid lg:relative grid-cols-1">
                    <div class="relative w-full shadow-t-xl lg:w-full">
                        <div class=" lg:h-[173px] lg:w-full rounded-lg rounded-b-none overflow-hidden">
                            <img :src="item.thumbnail" alt="Thumbnail da notícia" class="w-full h-full object-cover"/>
                        </div>
                    </div>
                    <div class="col-span-2 overflow-auto text-left shadow-none ">
                        <div class="grid grid-cols-2 ">

                            <div class="relative flex pt-3 ml-5 text-xs">
                                <p class="w-auto h-auto text-sm   rounded-lg  px-[20px] py-0.5 text-color-primary bg-color-primary/5">
                                    {{ displayTag }}
                                </p>
                            </div>
                            <div class="relative flex pt-3 ml-5 text-xs">
                                <p class="absolute py-1 ml-5 text-xs text-[#263238]">
                                    {{ formatData(item.publication_date) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="pt-3 pl-5">
                    <h2 class="text-xl font-bold text-color-primary">
                        {{ item.title }}
                    </h2>
                </div>
                <div class="pt-1 pl-5 w-[250px] text-left">
                    <h2 class="text-xs  text-[#263238]">
                        {{ item.subtitle }}
                    </h2>
                </div>
            </div>
        </div>
    </div>

    <!-- mobile -->
    <div class="hidden lg:hidden w-full min-w-[310px] overflow-auto">
        <div class="flex pt-6 pb-6 overflow-x-auto snap-x snap-mandatory lg:hidden ">
            <div v-for="(item, indexMobile) in news" :key="indexMobile"
                 class="w-[260px] snap-center shrink-0 mx-6  ">

                <div class="inline-block w-auto ">
                    <a class="cursor-pointer " @click="redirectToNews(item)">
                        <div
                            class="rounded-b-lg shadow-md hover:shadow-xl hover:-translate-y-1 transition-transform h-[342px] bg-white rounded-t-lg w-[276px]"
                        >
                            <div class="text-[#0400A1] place-items-around lg:w-full grid lg:relative  grid-cols-1">
                                <div class="relative w-full shadow-t-xl lg:w-full">
                                    <div class="h-[173px] w-full rounded-lg rounded-b-none overflow-hidden ">
                                        <img :src="item.thumbnail" alt="Thumbnail da notícia"
                                             class="w-full h-full object-cover"/>
                                    </div>
                                </div>
                                <div class="col-span-2 overflow-auto text-left shadow-none ">
                                    <div class="grid grid-cols-2 ">

                                        <div class="relative flex pt-3 ml-5 text-xs">
                                            <p class="w-auto h-auto text-sm  rounded-lg  px-[20px] py-0.5 text-color-primary bg-color-primary/5">
                                                {{ props.attr?.tags }}
                                            </p>
                                        </div>
                                        <div class="relative flex pt-3 ml-5 text-xs">
                                            <p class="absolute py-1 ml-5 text-xs text-[#263238] ">
                                                {{ formatData(item.publication_date) }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="pt-3 pl-5">
                                <h2 class="text-xl font-bold text-color-primary line-clamp-1">
                                    {{ item.title }} dsf dsfsd fs
                                </h2>
                            </div>
                            <div class="pt-1 pl-5 w-[250px] text-left">
                                <h2 class="text-xs  text-[#263238]">
                                    {{ item.subtitle }}
                                </h2>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.line-clamp-custom {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
}

.line-clamp-1 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
}
</style>
